import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService } from '@brighthub/api';
import { environment } from '@brighthub/environment';
import type {
  ChatSuggestionsAdminDetailHttpInput,
  ChatSuggestionsAdminListHttpInput,
  ChatSuggestionsAdminMutationHttpInput,
  ChatSuggestionsApiResponse,
  UpdateChatSuggestionBody,
} from '../models/chat-suggestions-api.model';
import type {
  ChatSuggestionAdminRow,
  CreateChatSuggestionRequest,
  UpdateChatSuggestionRequest,
} from '../models/chat-suggestions-admin.model';
import {
  extractAdminSuggestionList,
  extractAdminSuggestionOne,
} from './chat-suggestions-envelope.util';
import { ChatSuggestionsService } from './chat-suggestions.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

/**
 * Admin CRUD for fa-app-service **prototype chat suggestions** via {@link ApiService} on
 * `brightnav-api` (`/v1/prototype/chat/suggestions`).
 *
 * @remarks
 * Requires an Okta **access** token whose `groups` claim includes the group configured on
 * fa-app-service as **`BRIGHT_AI_ADMIN_ROLE`**. For developer-only admin, set that env var to the
 * same string as **`BRIGHTNAV_ADMIN_DEVELOPER_OKTA_GROUP`** from `@brighthub/admin-contract`
 * (e.g. `BrightNav_dev_admin_developers`), matching the group used on the `/brightnav/admin` route.
 * JWT verification is expected at API Gateway in front of ECS, not in the browser.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatSuggestionsAdminService {
  private readonly _api = inject(ApiService);
  private readonly _sessionSuggestions = inject(ChatSuggestionsService);

  /** Relative path under `environment.apiEndpoints['brightnav-api']`, without leading slash issues. */
  private static readonly suggestionsPath = '/v1/prototype/chat/suggestions';

  private suggestionsApiConfigured(): boolean {
    return Boolean(environment.apiEndpoints['brightnav-api']);
  }

  /**
   * Lists all suggestions including inactive (`GET ?includeInactive=true`).
   *
   * @param agentId When set, scopes the list to one agent (`&agentId=`).
   * @returns Rows with `active` present; empty array when API base is not configured.
   */
  listIncludeInactive(agentId?: string): Observable<ChatSuggestionAdminRow[]> {
    if (!this.suggestionsApiConfigured()) {
      return of([]);
    }
    let path = `${ChatSuggestionsAdminService.suggestionsPath}?includeInactive=true`;
    if (agentId?.trim()) {
      path += `&agentId=${encodeURIComponent(agentId.trim())}`;
    }
    return this._api.get<ChatSuggestionsAdminListHttpInput>('brightnav-api', path).pipe(
      map((res) => extractAdminSuggestionList(res)),
      catchError((err) => this.rethrowHttp(err))
    );
  }

  /**
   * Fetches one suggestion by stable key (`GET /:key`, admin shape with `active`).
   *
   * @param key Stable suggestion id from the API.
   */
  getByKey(key: string): Observable<ChatSuggestionAdminRow> {
    if (!this.suggestionsApiConfigured()) {
      return throwError(() => new HttpErrorResponse({ status: 0, statusText: 'API not configured' }));
    }
    const path = `${ChatSuggestionsAdminService.suggestionsPath}/${encodeURIComponent(key)}`;
    return this._api.get<ChatSuggestionsAdminDetailHttpInput>('brightnav-api', path).pipe(
      map((res) => extractAdminSuggestionOne(res)),
      switchMap((row) =>
        row
          ? of(row)
          : throwError(() => new HttpErrorResponse({ status: 404, statusText: 'Not found' }))
      ),
      catchError((err) => this.rethrowHttp(err))
    );
  }

  /**
   * Creates a suggestion (`POST`). Clears consumer session chips cache on success.
   *
   * @param body Create payload per fa-app-service contract.
   */
  create(body: CreateChatSuggestionRequest): Observable<void> {
    if (!this.suggestionsApiConfigured()) {
      return throwError(() => new HttpErrorResponse({ status: 0, statusText: 'API not configured' }));
    }
    return this._api
      .post<ChatSuggestionsAdminMutationHttpInput>('brightnav-api', ChatSuggestionsAdminService.suggestionsPath, body)
      .pipe(
        tap(() => this._sessionSuggestions.clearSessionCache()),
        map(() => undefined),
        catchError((err) => this.rethrowHttp(err))
      );
  }

  /**
   * Replaces fields for one suggestion (`PUT /:key`). Clears consumer session chips cache on success.
   *
   * @param key Stable id in the URL path.
   * @param body Update payload; set `clearVersion` to omit `version` in JSON so the backend clears it.
   */
  update(key: string, body: UpdateChatSuggestionRequest): Observable<void> {
    if (!this.suggestionsApiConfigured()) {
      return throwError(() => new HttpErrorResponse({ status: 0, statusText: 'API not configured' }));
    }
    const path = `${ChatSuggestionsAdminService.suggestionsPath}/${encodeURIComponent(key)}`;
    const jsonBody = this.serializeUpdateBody(body);
    return this._api.put<ChatSuggestionsAdminMutationHttpInput>('brightnav-api', path, jsonBody).pipe(
      tap(() => this._sessionSuggestions.clearSessionCache()),
      map(() => undefined),
      catchError((err) => this.rethrowHttp(err))
    );
  }

  /**
   * Hard-deletes a suggestion (`DELETE /:key`). Clears consumer session chips cache on success.
   *
   * @param key Stable id in the URL path.
   */
  delete(key: string): Observable<void> {
    if (!this.suggestionsApiConfigured()) {
      return throwError(() => new HttpErrorResponse({ status: 0, statusText: 'API not configured' }));
    }
    const path = `${ChatSuggestionsAdminService.suggestionsPath}/${encodeURIComponent(key)}`;
    return this._api.del<ChatSuggestionsApiResponse>('brightnav-api', path).pipe(
      tap(() => this._sessionSuggestions.clearSessionCache()),
      map(() => undefined),
      catchError((err) => this.rethrowHttp(err))
    );
  }

  private serializeUpdateBody(body: UpdateChatSuggestionRequest): UpdateChatSuggestionBody {
    const base = {
      agentId: body.agentId,
      cta: body.cta,
      macro: body.macro,
      active: body.active,
    };
    if (body.clearVersion) {
      return base;
    }
    if (body.version !== undefined) {
      return { ...base, version: body.version };
    }
    return base;
  }

  private rethrowHttp(err: unknown): Observable<never> {
    return throwError(() => err);
  }
}
